import React, { useCallback, useMemo, useId } from 'react';
import { CircularProgress, Box, Tooltip, Typography } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  ContentCopyOutlined as DuplicateIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { Product } from '../../../types/product.types';
import { useProductTable, UseProductTableProps } from './ProductTable.hooks';
import { LoadingContainer } from './ProductTable.styles';
import { BaseTable, TableColumn } from '../../common/Table';
import { StatusChip, ActionButton } from '../../common/Table/Table.styles';
import { catalogActionIconButtonSx, catalogActionSvgIconGradientSx } from '../catalogGradients';
import { CatalogActionIconGradientDefs } from '../CatalogActionIconGradientDefs';
import { formatWeightGrams } from '../../../utils/localeNumber';
import { formatDateTime } from '../../../utils/formatters/date';
import { STATUS_COLUMN_SORTABLE } from '../../../constants/listSort.constants';

export interface ProductCatalogActionPermissions {
  canViewRow?: boolean;
  canEdit?: boolean;
  canDuplicate?: boolean;
  canDelete?: boolean;
  /** Columna Proveedor en la tabla (field flag del menú catálogo) */
  canShowSupplierColumn?: boolean;
}

export interface ProductTableProps extends UseProductTableProps {
  loading: boolean;
  className?: string;
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc';
  onRequestSort?: (sortField: string) => void;
  actionPermissions?: ProductCatalogActionPermissions;
}

const ProductTableComponent: React.FC<ProductTableProps> = ({
  products,
  loading,
  selectedProducts,
  setSelectedProducts,
  onViewProduct,
  onEditProduct,
  onDuplicateProduct,
  onDeleteProduct,
  className,
  sortBy = null,
  sortOrder = 'asc',
  onRequestSort,
  actionPermissions,
}) => {
  const {
    canViewRow = true,
    canEdit = true,
    canDuplicate = true,
    canDelete = true,
    canShowSupplierColumn = true,
  } = actionPermissions ?? {};
  const {
    getStatusDisplay,
    getStatusColor,
    renderPrice,
    handleViewProduct,
    handleEditProduct,
    handleDuplicateProduct,
    handleDeleteProduct,
  } = useProductTable({
    products,
    selectedProducts,
    setSelectedProducts,
    onViewProduct,
    onEditProduct,
    onDuplicateProduct,
    onDeleteProduct,
  });

  const allColumns: TableColumn<Product>[] = useMemo(
    () => [
      {
        id: 'code',
        label: 'Código',
        sortField: 'code',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.code}
          </Typography>
        ),
      },
      {
        id: 'name',
        label: 'Nombre del producto',
        sortField: 'name',
        render: (item) => (
          <Typography color="text.primary" fontWeight={600} fontSize="1rem">
            {item.name}
          </Typography>
        ),
      },
      {
        id: 'type',
        label: 'Tipo',
        sortField: 'categoryName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.type}
          </Typography>
        ),
      },
      {
        id: 'weight',
        label: 'Peso',
        sortField: 'weightGrams',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.weight != null && item.weight !== '' ? formatWeightGrams(item.weight) : '—'}
          </Typography>
        ),
      },
      {
        id: 'price',
        label: 'Precio de Venta',
        sortField: 'salePrice',
        render: (item) => (
          <Typography color="success.main" fontWeight={600} fontSize="1rem">
            {renderPrice(item.price)}
          </Typography>
        ),
      },
      {
        id: 'inStock',
        label: 'En Stock',
        sortField: 'inStock',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.inStock ?? 0}
          </Typography>
        ),
      },
      {
        id: 'supplier',
        label: 'Proveedor',
        sortField: 'supplierName',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {typeof (item as any).supplier === 'object'
              ? (item as any).supplier?.name || '—'
              : (item as any).supplier || '—'}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Estado',
        sortField: 'status',
        sortable: STATUS_COLUMN_SORTABLE,
        render: (item) => (
          <StatusChip
            label={getStatusDisplay(item.status)}
            size="small"
            color={getStatusColor(item.status) as any}
          />
        ),
      },
      {
        id: 'updatedAt',
        label: 'Última actualización',
        sortField: 'updatedAt',
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.updatedAt ? formatDateTime(item.updatedAt) : '—'}
          </Typography>
        ),
      },
    ],
    [getStatusDisplay, getStatusColor, renderPrice],
  );

  const columns = useMemo(
    () => (canShowSupplierColumn ? allColumns : allColumns.filter((col) => col.id !== 'supplier')),
    [allColumns, canShowSupplierColumn],
  );

  const handleSort = useCallback(
    (field: string) => {
      if (onRequestSort != null) {
        onRequestSort(field);
      }
    },
    [onRequestSort],
  );

  const handleSelectItems = useCallback(
    (items: (string | number)[]) => setSelectedProducts(items as number[]),
    [setSelectedProducts],
  );

  const catalogIconGradientId = `catalog-action-grad-${useId().replace(/:/g, '')}`;
  const iconGradSx = catalogActionSvgIconGradientSx(catalogIconGradientId);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <>
      <CatalogActionIconGradientDefs id={catalogIconGradientId} />
      <BaseTable<Product>
        className={className}
        items={products}
        columns={columns}
        selectedItems={selectedProducts}
        onSelectItems={handleSelectItems}
        showCheckbox={canDelete}
        showActions={canViewRow || canEdit || canDuplicate || canDelete}
        getItemId={(item) => item.id}
        emptyStateTitle="No se encontraron productos"
        emptyStateSubtitle="Ajuste los filtros o agregue un nuevo producto."
        sortBy={sortBy}
        sortOrder={sortOrder}
        onRequestSort={onRequestSort != null ? handleSort : undefined}
        renderActions={(product) => {
          const actions = (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {canViewRow && (
                <Tooltip title="Ver detalles" arrow>
                  <ActionButton
                    size="small"
                    onClick={() => handleViewProduct(product.id)}
                    sx={catalogActionIconButtonSx}
                  >
                    <ViewIcon fontSize="small" sx={iconGradSx} />
                  </ActionButton>
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip title="Editar" arrow>
                  <ActionButton
                    size="small"
                    onClick={() => handleEditProduct(product.id)}
                    sx={catalogActionIconButtonSx}
                  >
                    <EditIcon fontSize="small" sx={iconGradSx} />
                  </ActionButton>
                </Tooltip>
              )}
              {canDuplicate && (
                <Tooltip title="Duplicar" arrow>
                  <ActionButton
                    size="small"
                    onClick={() => handleDuplicateProduct(product.id)}
                    sx={catalogActionIconButtonSx}
                  >
                    <DuplicateIcon fontSize="small" sx={iconGradSx} />
                  </ActionButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Eliminar" arrow>
                  <ActionButton
                    size="small"
                    onClick={() => handleDeleteProduct(product.id)}
                    sx={catalogActionIconButtonSx}
                  >
                    <DeleteIcon fontSize="small" sx={iconGradSx} />
                  </ActionButton>
                </Tooltip>
              )}
            </Box>
          );
          return actions;
        }}
      />
    </>
  );
};

export default ProductTableComponent;
